import axios from "axios";
import moment from "moment";
import { message } from "antd";
import { saveAs } from "file-saver";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import Compress from "./Compress";
import construirExcelDocumentos from "./ExcelDocumentosMasivo";
import construirExcelDocumento from "./ExcelDocumentoIndividual";
import construirExcelRequisicion from "./ExcelRequisicion";
import construirExcelRequisiciones from "./ExcelRequisicionesMasivo";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

/**
 * Descarga masiva de los documentos seleccionados en un listado (cotizaciones,
 * pedidos, embarques, ventas, órdenes de compra y requisiciones): un ZIP con el
 * PDF de cada uno, el XML de los facturados, un Excel por documento y/o el
 * Excel de resumen de dos hojas.
 *
 * El PDF y el Excel se arman AQUÍ, en el navegador, con los mismos generadores
 * de `src/Pdf` que usa la descarga individual: el servidor solo manda los datos.
 * Eso deja el costo del render fuera del servidor y hace que el archivo del ZIP
 * sea exactamente el mismo documento que se baja de uno en uno.
 *
 * Aquí vive también `descargarExcelDocumento`, que es lo que usan los botones de
 * las pantallas "Ver" para bajar el Excel de un solo documento.
 */

// Ids por petición. El endpoint tiene su propio tope de 100; esto mantiene cada
// respuesta chica y da avance visible mientras se descarga.
const TAMANO_TANDA = 20;

// pdfmake 0.1.x renderiza en el hilo principal: sin ceder el hilo cada tantos
// documentos, la pestaña se congela hasta terminar el lote.
const DOCUMENTOS_ENTRE_PAUSAS = 5;

const ESTADOS_COTIZACION = {
  0: "Cancelada", 1: "Vigente", 2: "Pendiente", 3: "Venta", 4: "Pedido", 7: "Vencida",
};
const ESTADOS_PEDIDO = { 0: "Cancelado", 1: "Vigente", 2: "Pendiente", 3: "Venta" };
const ESTADOS_EMBARQUE = { 0: "Cancelado", 1: "Vigente", 2: "Pendiente", 3: "Venta" };
const ESTADOS_REQUISICION = { 0: "Cancelada", 1: "Pendiente", 2: "En proceso", 3: "Terminada" };
const ESTADOS_ORDEN_COMPRA = { 1: "Sin recibir", 2: "Recibido parcialmente", 3: "Recibido" };

/** Mismas etiquetas que pinta la columna Estado del listado de órdenes de compra. */
const estadoOrdenDeCompra = (values) => {
  const etiquetas = [];
  if (values.finiquitada) etiquetas.push("Finiquitada");
  if (values.aprobada === false && values.estado !== 0) etiquetas.push("Por aprobar");
  if (values.estado === 0) etiquetas.push(values.ordenAgrupadaId ? "Agrupada" : "Cancelada");
  else if (ESTADOS_ORDEN_COMPRA[values.estado]) etiquetas.push(ESTADOS_ORDEN_COMPRA[values.estado]);
  return etiquetas.join(" / ");
};

const estadoRequisicion = (values) => {
  const estado = ESTADOS_REQUISICION[values?.data?.estado] || "";
  return values?.data?.finiquitada ? [estado, "Finiquitada"].filter(Boolean).join(" / ") : estado;
};

const estadoVenta = (values) => {
  if (values.estado === 0) return values.facturaId ? "Facturada" : "Cancelada";
  if (values.estado === 1) return "Sin entregar";
  if (values.estado === 2) return "Entregada parcial";
  if (values.estado === 3) return "Entregada";
  return "";
};

export const MODULOS = {
  cotizacion: {
    endpoint: "cotizaciones/datosPdfMasivo",
    endpointIndividual: "cotizaciones/datosPdf",
    campoId: "cotizacionId",
    nombreZip: "Cotizaciones",
    titulo: () => "Cotizacion",
    tieneXml: false,
    esVenta: false,
    serieFallback: () => "C",
    conRfc: () => true,
    etiquetaEstado: (values) => ESTADOS_COTIZACION[values.estado] || "",
    cargarGenerador: async () => {
      const modulo = await import("../Pdf/cotizacionPdf");
      return (values) => modulo.default(values);
    },
  },
  pedido: {
    endpoint: "pedidos/datosPdfMasivo",
    endpointIndividual: "pedidos/datosPdf",
    campoId: "pedidoId",
    nombreZip: "Pedidos",
    titulo: () => "Pedido",
    tieneXml: false,
    esVenta: false,
    serieFallback: () => "P",
    conRfc: () => true,
    etiquetaEstado: (values) => ESTADOS_PEDIDO[values.estado] || "",
    cargarGenerador: async () => {
      const modulo = await import("../Pdf/pedidoPdf");
      return (values) => modulo.default(values);
    },
  },
  embarque: {
    endpoint: "embarques/datosPdfMasivo",
    endpointIndividual: "embarques/datosPdf",
    campoId: "embarqueId",
    nombreZip: "Embarques",
    titulo: () => "Embarque",
    tieneXml: false,
    esVenta: false,
    // Mismo prefijo que usa hoy la descarga individual del embarque.
    serieFallback: () => "P",
    conRfc: () => true,
    etiquetaEstado: (values) => ESTADOS_EMBARQUE[values.estado] || "",
    cargarGenerador: async () => {
      const modulo = await import("../Pdf/embarquePdf");
      return (values) => modulo.default(values);
    },
  },
  venta: {
    endpoint: "ventas/datosPdfMasivo",
    endpointIndividual: "ventas/datosPdf",
    campoId: "ventaId",
    nombreZip: "Ventas",
    titulo: (values) => (values.factura ? "Factura" : "Remision"),
    tieneXml: true,
    esVenta: true,
    // La venta facturada baja como factura y la que no, como remisión: mismos
    // prefijos y misma regla del RFC que `VerVenta.descargarPDF`.
    serieFallback: (values) => (values.factura ? "F" : "R"),
    conRfc: (values) => !!values.factura,
    etiquetaEstado: estadoVenta,
    // `respuestaFactura.data` es el `dataFactura` de la venta, donde vive el UUID.
    obtenerUuid: (values) => values?.respuestaFactura?.data?.uuid || "",
    cargarGenerador: async () => {
      const [factura, remision] = await Promise.all([
        import("../Pdf/facturaPdf"),
        import("../Pdf/remisionPdf"),
      ]);
      return (values) => (values.factura ? factura.default(values) : remision.default(values));
    },
  },
  ordenDeCompra: {
    endpoint: "ordenesdecompra/datosPdfMasivo",
    endpointIndividual: "ordenesdecompra/datosPdf",
    campoId: "ordenId",
    nombreZip: "OrdenesDeCompra",
    titulo: () => "Orden de compra",
    tieneXml: false,
    esVenta: false,
    // El receptor del documento es el proveedor, no un cliente: cambia las
    // etiquetas de los dos Excel.
    esCompra: true,
    // Bajar el PDF marca la orden como descargada, igual que la descarga
    // individual (el listado lo muestra con un Tag).
    marcaDescarga: true,
    serieFallback: () => "OC",
    conRfc: () => true,
    etiquetaEstado: estadoOrdenDeCompra,
    cargarGenerador: async () => {
      const modulo = await import("../Pdf/ordenCompraPdf");
      return (values) => modulo.default(values);
    },
  },
  requisicion: {
    endpoint: "requisiciones/verRequisicionesPdfMasivo",
    endpointIndividual: "requisiciones/verRequisicionesPdf",
    campoId: "requisicionId",
    nombreZip: "Requisiciones",
    titulo: () => "Requisicion",
    tieneXml: false,
    esVenta: false,
    etiquetaEstado: estadoRequisicion,
    // La requisición no tiene CFDI: ni el nombre del archivo ni los Excel se
    // pueden sacar de un `dataSat`, así que trae los suyos.
    nombreArchivo: (values) => {
      const numero = [values?.data?.serie, values?.data?.numero].filter(Boolean).join(" ");
      const proyecto = values?.proyecto?.nombre;
      return "Requisicion - " + numero + (proyecto ? " ( " + proyecto + " )" : "");
    },
    construirExcel: (values) => construirExcelRequisicion(values),
    construirExcelResumen: (documentos, modulo) => construirExcelRequisiciones(documentos, modulo),
    cargarGenerador: async () => {
      const modulo = await import("../Pdf/requisicionPdf");
      return (values) => modulo.default(values);
    },
  },
};

const enTandas = (arreglo, tamano) => {
  const tandas = [];
  for (let i = 0; i < arreglo.length; i += tamano) tandas.push(arreglo.slice(i, i + tamano));
  return tandas;
};

const cederElHilo = () => new Promise((resolve) => setTimeout(resolve, 0));

// JSZip trata la diagonal como separador de carpetas, y la razón social del
// receptor bien puede traer una.
const limpiarNombreArchivo = (texto) => (texto || "").replace(/[/\\?*[\]:#]/g, "_");

/**
 * `JSZip.file()` sobreescribe en silencio si el nombre se repite, y dos
 * documentos sin serie con el mismo folio generan el mismo nombre.
 */
const nombreUnico = (nombre, usados) => {
  if (!usados.has(nombre)) {
    usados.add(nombre);
    return nombre;
  }
  let intento = 2;
  while (usados.has(`${nombre} (${intento})`)) intento += 1;
  const unico = `${nombre} (${intento})`;
  usados.add(unico);
  return unico;
};

const nombreDocumento = (values, modulo) => {
  // Los módulos sin CFDI (la requisición) arman su propio nombre.
  if (modulo.nombreArchivo) return limpiarNombreArchivo(modulo.nombreArchivo(values));

  const dataSat = values?.respuestaFactura?.dataSat || {};
  const receptor = dataSat.Receptor || {};
  const serie = dataSat.Serie ? dataSat.Serie : modulo.serieFallback(values);
  const conRfc = modulo.conRfc(values);
  return limpiarNombreArchivo(
    serie + " - " + dataSat.Folio +
    " ( " + (conRfc ? receptor.Rfc + " - " : "") + receptor.Nombre + " )"
  );
};

/**
 * El backend manda el base64 de cada logo una sola vez para todo el lote: aquí
 * se vuelve a pegar en cada documento antes de generar su PDF.
 */
const rehidratarLogos = (values, logos) => {
  const pegar = (contenedor, campo) => {
    const logo = contenedor ? contenedor[campo] : null;
    if (!logo || !logo.claveLogo) return;
    const guardado = logos ? logos[logo.claveLogo] : null;
    if (!guardado) return;
    contenedor[campo] = {
      ...logo,
      base64: guardado.base64,
      base64Completa: guardado.base64Completa,
    };
  };
  pegar(values?.respuestaFactura?.empresa, "logo");
  // La requisición cuelga la empresa de la raíz, no de `respuestaFactura`.
  pegar(values?.empresa, "logo");
  pegar(values, "logoBiwo");
};

/** El Excel de un documento; cada módulo puede traer el suyo. */
const excelDeDocumento = (values, modulo) =>
  modulo.construirExcel ? modulo.construirExcel(values, modulo) : construirExcelDocumento(values, modulo);

/** El Excel de resumen de la selección; cada módulo puede traer el suyo. */
const excelDeResumen = (documentos, modulo) =>
  modulo.construirExcelResumen
    ? modulo.construirExcelResumen(documentos, modulo)
    : construirExcelDocumentos(documentos, modulo);

const generarPdfBlob = (docDefinition) =>
  new Promise((resolve, reject) => {
    try {
      pdfMake.createPdf(docDefinition).getBlob((blob) => resolve(blob));
    } catch (error) {
      reject(error);
    }
  });

/**
 * @param {Object} params
 * @param {'cotizacion'|'pedido'|'embarque'|'venta'} params.modulo
 * @param {string[]} params.ids            ids de los documentos seleccionados
 * @param {string} params.empresaId
 * @param {Object} params.headersToken
 * @param {boolean} params.incluirPdf
 * @param {boolean} params.incluirXml             solo aplica en Ventas
 * @param {boolean} params.incluirExcelDocumento  un .xlsx por documento
 * @param {boolean} params.incluirExcel           el .xlsx de resumen (2 hojas)
 * @param {Function} [params.onProgreso]   (hechos, total) => void
 * @returns {Promise<boolean>} true si se descargó algo
 */
const descargarDocumentosMasivo = async ({
  modulo: claveModulo, ids, empresaId, headersToken,
  incluirPdf = true, incluirXml = false, incluirExcelDocumento = false,
  incluirExcel = false, onProgreso,
}) => {
  const modulo = MODULOS[claveModulo];
  if (!modulo) {
    message.error("Módulo desconocido para la descarga masiva");
    return false;
  }
  if (!ids || ids.length === 0) {
    message.info("No hay documentos seleccionados");
    return false;
  }
  if (!incluirPdf && !incluirXml && !incluirExcelDocumento && !incluirExcel) {
    message.info("Elige al menos un formato para descargar");
    return false;
  }

  const archivos = [];
  const nombresUsados = new Set();
  const valuesDescargados = [];
  let fallidos = 0;
  let hechos = 0;

  try {
    // Los generadores de PDF pesan varios cientos de KB: se cargan bajo demanda
    // para no meterlos al bundle de los listados. El XML y el Excel no los
    // necesitan.
    const generar = incluirPdf ? await modulo.cargarGenerador() : null;

    // El Excel por documento también lleva el logo de la empresa; el de resumen
    // y el XML no lo necesitan, y ahí conviene no cargar los base64.
    const necesitaLogos = incluirPdf || incluirExcelDocumento;

    for (const tanda of enTandas(ids, TAMANO_TANDA)) {
      const res = await axios.post(
        modulo.endpoint,
        {
          empresaId,
          ids: tanda,
          incluirLogos: necesitaLogos,
          // Solo bajar el PDF cuenta como "descargado": pedir nada más el Excel
          // no debería marcar el documento.
          ...(modulo.marcaDescarga ? { descarga: incluirPdf } : {}),
        },
        { headers: headersToken }
      );

      if (res.data.success !== 2) {
        message.error(res.data.message || "Sucedió un error al preparar la descarga");
        return false;
      }

      for (const documento of res.data.documentos) {
        if (!documento.ok) {
          fallidos += 1;
          hechos += 1;
          if (onProgreso) onProgreso(hechos, ids.length);
          continue;
        }

        const values = documento.values;
        rehidratarLogos(values, res.data.logos);
        valuesDescargados.push(values);

        const nombre = nombreUnico(nombreDocumento(values, modulo), nombresUsados);

        if (incluirXml && modulo.tieneXml && values.factura) {
          const xml = values?.respuestaFactura?.data?.cfdi;
          if (xml) archivos.push({ nombre: `${nombre}.xml`, contenido: xml });
        }

        if (incluirPdf) {
          try {
            const blob = await generarPdfBlob(generar(values));
            archivos.push({ nombre: `${nombre}.pdf`, contenido: blob });
          } catch (error) {
            console.error("Error al generar el PDF de", documento.id, error);
            fallidos += 1;
          }
        }

        if (incluirExcelDocumento) {
          try {
            const blob = await excelDeDocumento(values, modulo);
            archivos.push({ nombre: `${nombre}.xlsx`, contenido: blob });
          } catch (error) {
            console.error("Error al generar el Excel de", documento.id, error);
            fallidos += 1;
          }
        }

        hechos += 1;
        if (onProgreso) onProgreso(hechos, ids.length);
        if (hechos % DOCUMENTOS_ENTRE_PAUSAS === 0) await cederElHilo();
      }
    }

    if (incluirExcel && valuesDescargados.length > 0) {
      archivos.push({
        // "Resumen_" para que no se confunda con los .xlsx por documento.
        nombre: `Resumen_${modulo.nombreZip}.xlsx`,
        contenido: excelDeResumen(valuesDescargados, modulo),
      });
    }

    if (archivos.length === 0) {
      message.warning("No se pudo generar ningún archivo con lo seleccionado");
      return false;
    }

    Compress(archivos, `${modulo.nombreZip}_${moment().format("YYYY-MM-DD")}`);

    if (fallidos > 0) {
      message.warning(
        fallidos === 1
          ? "1 documento no se pudo generar y quedó fuera del ZIP"
          : `${fallidos} documentos no se pudieron generar y quedaron fuera del ZIP`
      );
    }
    return true;
  } catch (error) {
    console.error("Error en la descarga masiva de documentos:", error);
    message.error("Sucedió un error al descargar los documentos");
    return false;
  }
};

/**
 * Excel de UN documento, para el botón de las pantallas "Ver". Pide los mismos
 * datos que el PDF individual (`<modulo>/datosPdf`) y baja el .xlsx con el mismo
 * nombre con el que se baja el PDF.
 *
 * @param {Object} params
 * @param {'cotizacion'|'pedido'|'embarque'|'venta'|'ordenDeCompra'|'requisicion'} params.modulo
 * @param {string} params.id
 * @param {string} params.empresaId
 * @param {Object} params.headersToken
 * @returns {Promise<boolean>} true si se descargó
 */
export const descargarExcelDocumento = async ({ modulo: claveModulo, id, empresaId, headersToken }) => {
  const modulo = MODULOS[claveModulo];
  if (!modulo) {
    message.error("Módulo desconocido para la descarga");
    return false;
  }
  if (!id) {
    message.info("No hay documento para descargar");
    return false;
  }

  try {
    const res = await axios.post(
      modulo.endpointIndividual,
      { [modulo.campoId]: id, empresaId },
      { headers: headersToken }
    );

    // `verRequisicionesPdf` responde con `succes` (sin la segunda "s"); el resto
    // de los endpoints individuales usan `success`.
    if (res.data.success !== 2 && res.data.succes !== 2) {
      message.error(res.data.message || "Sucedió un error al preparar el Excel");
      return false;
    }

    const blob = await excelDeDocumento(res.data, modulo);
    saveAs(blob, `${nombreDocumento(res.data, modulo)}.xlsx`);
    return true;
  } catch (error) {
    console.error("Error al descargar el Excel del documento:", error);
    message.error("Sucedió un error al descargar el Excel");
    return false;
  }
};

export default descargarDocumentosMasivo;
